/**
 * Copyright (c) 2016-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql } from "@apollo/client";
import { DownloadLine, ExternalLinkLine } from "@ndla/icons";
import { figureApa7CopyString, metaTypes } from "@ndla/licenses";
import { SafeLinkButton } from "@ndla/safelink";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AddResourceToFolderModal } from "../../components/MyNdla/AddResourceToFolderModal";
import config from "../../config";
import type { GQLPodcastLicenseList_PodcastLicenseFragment } from "../../graphqlTypes";
import { useBasePathname } from "../../util/localePath";
import { FavoriteButton } from "../Article/FavoritesButton";
import {
  MediaList,
  MediaListItem,
  MediaListItemBody,
  MediaListItemActions,
  MediaListItemMeta,
  type ItemType,
  MediaListLicense,
  MediaListContent,
} from "../MediaList/MediaList";
import { CopyBlock } from "./CopyBlock";
import { CopyTextButton } from "./CopyTextButton";
import { licenseListCopyrightFragment } from "./licenseFragments";
import { downloadUrl, getGroupedContributorDescriptionList, isCopyrighted } from "./licenseHelpers";

interface PodcastLicenseInfoProps {
  podcast: GQLPodcastLicenseList_PodcastLicenseFragment;
}

const PodcastLicenseInfo = ({ podcast }: PodcastLicenseInfoProps) => {
  const { t, i18n } = useTranslation();
  const pathname = useBasePathname();

  const pageUrl = useMemo(() => `/audio/${podcast.id}`, [podcast.id]);

  const shouldShowLink = useMemo(() => pathname !== pageUrl, [pageUrl, pathname]);

  const items: ItemType[] = getGroupedContributorDescriptionList(podcast.copyright, t);

  const copyText = figureApa7CopyString(
    podcast.title,
    undefined,
    podcast.src,
    `${config.ndlaFrontendDomain}/audio/${podcast.id}`,
    podcast.copyright,
    podcast.copyright.license.license,
    "",
    t,
    i18n.language,
  );

  if (podcast.title) {
    items.unshift({
      label: t("title"),
      description: podcast.title,
      metaType: metaTypes.title,
    });
  }
  if (podcast.copyright.origin) {
    items.push({
      label: t("source"),
      description: podcast.copyright.origin,
      metaType: metaTypes.other,
    });
  }

  if (podcast.copyright.processed === true) {
    items.push({
      label: t("license.processed"),
      metaType: metaTypes.otherWithoutDescription,
    });
  }

  return (
    <MediaListItem>
      <MediaListItemBody
        license={podcast.copyright.license?.license}
        resourceType="podcast"
        resourceUrl={podcast.src}
        locale={i18n.language}
      >
        <MediaListContent>
          <MediaListLicense
            licenseType={podcast.copyright.license.license}
            title={t("license.podcast.rules")}
            sourceTitle={podcast.title}
            sourceType="podcast"
          >
            {!isCopyrighted(podcast.copyright.license.license) && (
              <AddResourceToFolderModal
                resource={{
                  id: podcast.id,
                  path: `/audio/${podcast.id}`,
                  resourceType: "audio",
                }}
              >
                <FavoriteButton path={`/audio/${podcast.id}`} />
              </AddResourceToFolderModal>
            )}
          </MediaListLicense>
          {!isCopyrighted(podcast.copyright.license.license) && (
            <MediaListItemActions>
              <SafeLinkButton to={downloadUrl(podcast.src)} download variant="secondary" size="small">
                <DownloadLine />
                {t("license.download")}
              </SafeLinkButton>
              <CopyTextButton
                stringToCopy={`<iframe title="${podcast.title}" aria-label="${podcast.title}" height="400" width="500" allowfullscreen="" style="border: none;" src="${podcast.src}"></iframe>`}
                copyTitle={t("license.embed")}
                hasCopiedTitle={t("license.embedCopied")}
              />
              {!!shouldShowLink && (
                <SafeLinkButton to={pageUrl} target="_blank" variant="secondary" rel="noopener noreferrer" size="small">
                  <ExternalLinkLine />
                  {t("license.openLink")}
                </SafeLinkButton>
              )}
            </MediaListItemActions>
          )}
        </MediaListContent>
        <MediaListItemActions>
          <MediaListContent>
            <MediaListItemMeta items={items} />
            <CopyBlock stringToCopy={copyText} license={podcast.copyright.license.license} />
          </MediaListContent>
        </MediaListItemActions>
      </MediaListItemBody>
    </MediaListItem>
  );
};

interface Props {
  podcasts: GQLPodcastLicenseList_PodcastLicenseFragment[];
}

export const PodcastLicenseList = ({ podcasts }: Props) => {
  return (
    <MediaList>
      {podcasts.map((podcast, index) => (
        <PodcastLicenseInfo podcast={podcast} key={`${podcast.id}-${index}`} />
      ))}
    </MediaList>
  );
};

PodcastLicenseList.fragments = {
  podcast: gql`
    fragment PodcastLicenseList_PodcastLicense on PodcastLicense {
      id
      src
      copyText
      title
      description
      copyright {
        origin
        ...LicenseListCopyright
      }
    }
    ${licenseListCopyrightFragment}
  `,
};
